// src/components/EmailTemplateModal.tsx
import React, { useState, useEffect } from 'react';

interface AdminUnderwriterData {
  id: string;
  name: string;
  email?: string;
  type: 'administrator' | 'underwriter';
  quotation_status?: string;
  premium?: string;
}

interface EmailTemplateModalProps {
  isVisible: boolean;
  subject: string;
  recipients: string[];
  administrators?: AdminUnderwriterData[];
  underwriters?: AdminUnderwriterData[];
  onSend: (emailData: {
    subject: string;
    message: string;
    includePDF: boolean;
  }) => void;
  onCancel: () => void;
  isLoading?: boolean;
  defaultMessage?: string;
  showInsurersNote?: boolean;
}

const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({
  isVisible,
  subject,
  recipients,
  administrators = [],
  underwriters = [],
  onSend,
  onCancel,
  isLoading = false,
  defaultMessage = ''
}) => {
  const [message, setMessage] = useState(defaultMessage);
  const [includePDF, setIncludePDF] = useState(true);
  const [emailSubject, setEmailSubject] = useState(subject);

  // Reset form when modal opens
  useEffect(() => {
    if (isVisible) {
      setMessage(defaultMessage);
      setIncludePDF(true);
      setEmailSubject(subject);
    }
  }, [isVisible, defaultMessage, subject]);

  if (!isVisible) return null;

  // Generate default message template
  const generateDefaultMessage = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    return `Dear Administrator,\n\nPlease find attached the quotation for your review.\n\nThis quotation was generated on ${dateStr} and contains all relevant product details.\n\nPlease review the attached PDF and let us know if you have any questions or require further information.\n\nBest regards,\nInsurance Management System`;
  };

  const handleUseTemplate = () => {
    setMessage(generateDefaultMessage());
  };

  const handleClearMessage = () => {
    setMessage('');
  };

  const handleSend = () => {
    if (!emailSubject.trim()) {
      alert('Please enter a subject for the email');
      return;
    }
    
    if (!message.trim()) {
      if (!window.confirm('The email message is empty. Send anyway?')) {
        return;
}
    }
    
    onSend({
      subject: emailSubject,
      message: message.trim(),
      includePDF
    });
  };

  const recipientCount = recipients.length;
  const hasValidRecipients = recipientCount > 0;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-800">Send Quotation Email</h3>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              disabled={isLoading}
            >
              ×
            </button>
          </div>
          
          <div className="text-sm text-gray-600 mb-4">
            Compose your email below. The quotation details will be included in the email body and as a PDF attachment.
          </div>
        </div>

        {/* Email Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Left Column - Recipients */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To ({recipientCount} recipient{recipientCount !== 1 ? 's' : ''})
              </label>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 max-h-40 overflow-y-auto">
                {hasValidRecipients ? (
                  <ul className="space-y-2">
                    {recipients.map((recipient, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                        <span className="truncate">{recipient}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-red-500 text-sm">
                    No valid email addresses found. Please check administrator settings.
                  </div>
                )}
              </div>
            </div>

            {/* Insurers Summary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Insurers Summary
              </label>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="space-y-3">
                  {administrators.length > 0 && (
                    <div>
                      <div className="font-medium text-blue-800 text-sm">Administrators:</div>
                      <div className="text-sm text-blue-700">
                        {administrators.map(admin => admin.name).join(', ')}
                      </div>
                    </div>
                  )}
                  
                  {underwriters.length > 0 && (
                    <div>
                      <div className="font-medium text-blue-800 text-sm">Underwriters:</div>
                      <div className="text-sm text-blue-700">
                        {underwriters.map(uw => uw.name).join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Settings */}
          <div className="space-y-4">
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Email subject"
                disabled={isLoading}
              />
            </div>

            {/* PDF Attachment Option */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includePDF}
                  onChange={(e) => setIncludePDF(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  disabled={isLoading}
                />
                <div>
                  <span className="font-medium text-gray-700">Include PDF Attachment</span>
                  <p className="text-sm text-gray-500 mt-1">
                    Attach a formatted PDF version of the quotation
                  </p>
                </div>
              </label>
            </div>

            {/* Message Template Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Template
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleUseTemplate}
                  className="px-3 py-2 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-200 transition-colors"
                  disabled={isLoading}
                >
                  Use Default Template
                </button>
                <button
                  type="button"
                  onClick={handleClearMessage}
                  className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isLoading}
                >
                  Clear Message
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Message Editor */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Email Message *
            </label>
            <div className="text-sm text-gray-500">
              {message.length} characters
            </div>
          </div>
          
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            placeholder="Type your email message here..."
            disabled={isLoading}
          />
          
          <div className="mt-2 text-sm text-gray-500">
            Tip: You can use the "Use Default Template" button for a pre-written message.
          </div>
        </div>

        {/* Preview Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Email Preview
            </label>
            <div className="text-sm text-gray-500">
              Shows how email will appear
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 max-h-60 overflow-y-auto">
            <div className="space-y-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">Subject:</div>
                <div className="font-medium">{emailSubject || '(No subject)'}</div>
              </div>
              
              <div>
                <div className="text-xs text-gray-500 mb-1">Message:</div>
                <div className="whitespace-pre-wrap text-sm">
                  {message || '(Message will be empty)'}
                </div>
              </div>
              
              <div>
                <div className="text-xs text-gray-500 mb-1">Attachments:</div>
                <div className="flex items-center gap-2">
                  {includePDF ? (
                    <>
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm">quotation.pdf</span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">No attachments</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 bg-gray-300 text-gray-800 font-medium rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          
          <button
            onClick={handleSend}
            disabled={isLoading || !hasValidRecipients}
            className={`px-6 py-2 font-medium rounded-lg flex items-center gap-2 transition-colors ${
              isLoading || !hasValidRecipients
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Email{includePDF ? ' with PDF' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateModal;